import React, { useState, useEffect, useCallback } from 'react';
import Country, { type CountryResponseArgs, convertCountryResponseToArgs } from '@this/domain/god/country';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal/modal';
import { Fetcher } from '@this/src/util';
import { ConfirmModal } from '@this/components/shared/ui/feedbacks/modal/confirm_modal';
import { Button } from '@this/shared/ui/inputs/button';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { Pagination } from '@this/shared/ui/navigations/pagination/pagination';
import { Input } from '@this/shared/ui/inputs/input';
import CountryForm from './country_form';
import { formatDate } from './utils';

function getStyles() {
  return {
    searchInput: {
      marginRight: '10px',
      marginLeft: '6px',
      fontSize: '13px',
      width: '150px'
    } as React.CSSProperties,
    inputAreaRight: {
      display: 'flex',
      marginBottom: '20px',
      alignItems: 'center'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      marginBottom: '20px'
    } as React.CSSProperties,
    actionButtonRow: {
      display: 'flex',
      gap: '8px'
    } as React.CSSProperties
  } as const;
}

interface ForeignRegionOption {
  id: number;
  name: string;
}

interface CountriesResponse {
  countries: CountryResponseArgs[];
  foreign_regions: ForeignRegionOption[];
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
}

const CountriesTab: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [foreignRegions, setForeignRegions] = useState<ForeignRegionOption[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [searchForeignRegionName, setSearchForeignRegionName] = useState<string>('');
  const [searchCountryName, setSearchCountryName] = useState<string>('');
  const [debouncedForeignRegionName, setDebouncedForeignRegionName] = useState<string>('');
  const [debouncedCountryName, setDebouncedCountryName] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newCountry, setNewCountry] = useState<Country | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Country | null>(null);
  const [editCountry, setEditCountry] = useState<Country | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<Country | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedForeignRegionName(searchForeignRegionName);
      setDebouncedCountryName(searchCountryName);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchForeignRegionName, searchCountryName]);

  const fetchCountries = useCallback(async () => {
    setLoading(true);
    const params: {
      page: number;
      per_page: number;
      foreign_region_name?: string;
      country_name?: string;
    } = {
      page: currentPage,
      per_page: pageSize
    };
    if (debouncedForeignRegionName) params.foreign_region_name = debouncedForeignRegionName;
    if (debouncedCountryName) params.country_name = debouncedCountryName;
    return Fetcher.get<CountriesResponse>('/god/countries', params).then(
      response => {
        const countryList = response.countries.map(data => new Country(convertCountryResponseToArgs(data)));
        setLoading(false);
        setCountries(countryList);
        setForeignRegions(response.foreign_regions);
        setTotalPages(response.total_pages || 1);
        setError(null);
      },
      () => {
        setLoading(false);
        setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
      }
    );
  }, [currentPage, pageSize, debouncedForeignRegionName, debouncedCountryName]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const openAddModal = useCallback(() => {
    setShowAddModal(true);
    setNewCountry(Country.createEmpty());
    setAddError(null);
  }, []);

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setNewCountry(null);
    setAddError(null);
  }, []);

  const handleAddCountry = useCallback(async () => {
    if (!newCountry) return;
    if (!newCountry.foreignRegionId) {
      setAddError('海外地域は必須です');
      return;
    }
    if (!newCountry.name) {
      setAddError('国名は必須です');
      return;
    }
    if (!newCountry.formalName) {
      setAddError('正式国名は必須です');
      return;
    }
    setLoading(true);
    setAddError(null);
    try {
      await Fetcher.post('/god/countries', newCountry.submitParams);
      closeAddModal();
      fetchCountries();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setAddError(err.response?.data?.error?.join('\n') || '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [newCountry, closeAddModal, fetchCountries]);

  const openEditModal = useCallback((country: Country) => {
    setShowEditModal(true);
    setEditTarget(country);
    setEditCountry(
      new Country({
        id: country.id,
        foreignRegionId: country.foreignRegionId,
        foreignRegionName: country.foreignRegionName,
        foreignRegionCode: country.foreignRegionCode,
        name: country.name,
        formalName: country.formalName,
        enName: country.enName,
        iso2code: country.iso2code,
        ministryOfForeignAffairsId: country.ministryOfForeignAffairsId,
        createdAt: country.createdAt,
        updatedAt: country.updatedAt
      })
    );
    setEditError(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditTarget(null);
    setEditCountry(null);
    setEditError(null);
  }, []);

  const handleEditCountryValidate = useCallback(() => {
    if (!editTarget || !editCountry) return;
    if (!editCountry.foreignRegionId) {
      setEditError('海外地域は必須です');
      return;
    }
    if (!editCountry.name) {
      setEditError('国名は必須です');
      return;
    }
    if (!editCountry.formalName) {
      setEditError('正式国名は必須です');
      return;
    }
    setShowEditConfirmModal(true);
  }, [editTarget, editCountry]);

  const handleEditCountrySubmit = useCallback(async () => {
    if (!editTarget || !editCountry) return;
    setShowEditConfirmModal(false);
    setLoading(true);
    setEditError(null);
    try {
      await Fetcher.put(`/god/countries/${editTarget.id}`, editCountry.submitParams);
      closeEditModal();
      fetchCountries();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setEditError(err.response?.data?.error?.join('\n') || '更新に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [editTarget, editCountry, closeEditModal, fetchCountries]);

  const openDeleteModal = useCallback((country: Country) => {
    setShowDeleteModal(true);
    setDeleteTarget(country);
    setDeleteError(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const handleDeleteCountry = useCallback(async () => {
    if (!deleteTarget) return;
    setLoading(true);
    setDeleteError(null);
    try {
      await Fetcher.delete(`/god/countries/${deleteTarget.id}`);
      closeDeleteModal();
      fetchCountries();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setDeleteError(err.response?.data?.error?.join('\n') || '削除に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [deleteTarget, closeDeleteModal, fetchCountries]);

  const styles = getStyles();

  return (
    <div>
      <div style={styles.inputAreaRight}>
        <label htmlFor="foreign-region-name-search">
          海外地域名検索:
          <Input
            id="foreign-region-name-search"
            value={searchForeignRegionName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchForeignRegionName(e.target.value)}
            style={{ ...styles.searchInput, marginLeft: '5px' }}
          />
        </label>
        <label style={{ marginLeft: 16 }} htmlFor="country-name-search">
          国名検索:
          <Input
            id="country-name-search"
            value={searchCountryName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchCountryName(e.target.value)}
            style={{ ...styles.searchInput, marginLeft: '5px' }}
          />
        </label>
        <Button size="small" onClick={openAddModal} style={{ marginLeft: 'auto' }}>
          新規追加
        </Button>
      </div>
      <div>
        {error && <div style={{ ...styles.error, whiteSpace: 'pre-wrap' }}>{error}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <Loading />
          </div>
        ) : (
          <>
            <TableContainer height="60vh">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="60px">ID</TableCell>
                    <TableCell width="120px">国名</TableCell>
                    <TableCell width="150px">正式国名</TableCell>
                    <TableCell width="120px">英語名</TableCell>
                    <TableCell width="80px">ISO2</TableCell>
                    <TableCell width="120px">海外地域</TableCell>
                    <TableCell width="100px">外務省ID</TableCell>
                    <TableCell width="150px">作成日時</TableCell>
                    <TableCell width="150px">更新日時</TableCell>
                    <TableCell width="120px">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {countries.length === 0 ? (
                    <TableRow>
                      <TableCell style={{ textAlign: 'center' }} colSpan={10}>
                        データがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    countries.map(country => (
                      <TableRow key={country.id}>
                        <TableCell>{country.id}</TableCell>
                        <TableCell>{country.name}</TableCell>
                        <TableCell>{country.formalName}</TableCell>
                        <TableCell>{country.enName || '-'}</TableCell>
                        <TableCell>{country.iso2code || '-'}</TableCell>
                        <TableCell>{country.foreignRegionName || '-'}</TableCell>
                        <TableCell>{country.ministryOfForeignAffairsId || '-'}</TableCell>
                        <TableCell>{formatDate(country.createdAt)}</TableCell>
                        <TableCell>{formatDate(country.updatedAt)}</TableCell>
                        <TableCell>
                          <div style={styles.actionButtonRow}>
                            <Button size="small" onClick={() => openEditModal(country)}>
                              編集
                            </Button>
                            <Button size="small" color="danger" onClick={() => openDeleteModal(country)}>
                              削除
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
            </div>
          </>
        )}
      </div>
      {newCountry && (
        <Modal open={showAddModal} onClose={() => closeAddModal()}>
          <ModalHeader>国 新規追加</ModalHeader>
          <ModalBody>
            <CountryForm
              country={newCountry}
              foreignRegions={foreignRegions}
              onSubmit={handleAddCountry}
              onCancel={closeAddModal}
              error={addError}
              submitLabel="登録"
              cancelLabel="キャンセル"
            />
          </ModalBody>
        </Modal>
      )}
      {editCountry && (
        <Modal open={showEditModal} onClose={() => closeEditModal()}>
          <ModalHeader>国 編集</ModalHeader>
          <ModalBody>
            <CountryForm
              country={editCountry}
              foreignRegions={foreignRegions}
              onSubmit={handleEditCountryValidate}
              onCancel={closeEditModal}
              error={editError}
              submitLabel="更新"
              cancelLabel="キャンセル"
            />
          </ModalBody>
        </Modal>
      )}
      <ConfirmModal
        open={showEditConfirmModal}
        onCancel={() => setShowEditConfirmModal(false)}
        onSubmit={handleEditCountrySubmit}
        description={['顧客の設定画面の表示も変更されます。', 'それでも変更しますか？']}
        submitButtonLabel="変更する"
      />
      <ConfirmModal
        open={showDeleteModal}
        onCancel={closeDeleteModal}
        onSubmit={handleDeleteCountry}
        description={
          deleteTarget
            ? deleteError
              ? [deleteError]
              : ['本当に削除しますか？', `国名: ${deleteTarget.name}`]
            : ''
        }
        color="danger"
        submitButtonLabel="削除"
      />
    </div>
  );
};

export default CountriesTab;
